<template>
    <div class="pd-salary-view" v-if="pd">
        <template v-if="!salary.ready">
            <section-header title="Your Next Column" icon="fa-chart-line-up"/>
            <ui-card class="mb-6">
                <empty-state variant="card" icon="fa-circle-info" text="Add your district, degree and current clock-hour column and we'll show how close you are to your next salary column, and what it's worth.">
                    <b-button type="is-primary" size="is-small" tag="router-link" :to="settingsRoute"><i class="fas fa-plus mr-2"></i>Add Your Details</b-button>
                </empty-state>
            </ui-card>
        </template>

        <template v-else>
            <!-- The reachable columns -->
            <template v-if="targets.length">
                <section-header title="Your Next Column" icon="fa-chart-line-up" :count="targets.length"/>
                <ui-card class="mb-6">
                    <p class="cards-intro">From <strong>{{ position.current.tier }}</strong> at {{ position.exp }} {{ position.exp === 1 ? 'year' : 'years' }} of experience, clock hours can move you to any of these columns on {{ salary.district_name }}'s schedule. <router-link :to="settingsRoute">Update your details</router-link> if that's not right.</p>
                    <lane-target-cards :targets="targets" :selected="chartTarget" @select="chartTarget = $event"/>
                </ui-card>
            </template>

            <!-- Where you are / could be / what waiting costs, plus the CBA
                 ask when the schedule is missing or outdated -->
            <section-header :title="scale ? 'What Moving Up Is Worth' : 'Salary Schedule'" icon="fa-stairs"/>
            <ui-card class="mb-6">
                <package-pitch
                    :scale="scaleLoading ? null : scale"
                    :scale-loading="scaleLoading"
                    :has-district="true"
                    :district-id="salary.district_id"
                    :district-name="salary.district_name || ''"
                    :position="position"
                    :target="chartTarget"
                    :pkg-hours="0"
                />
            </ui-card>

            <!-- The published grid -->
            <template v-if="scale">
                <section-header :title="salary.district_name + ' Salary Schedule'" icon="fa-table-cells">
                    <a v-if="scale.source_url" :href="scale.source_url" target="_blank" rel="noopener" class="section-link">{{ scale.start_year }}-{{ scale.end_year }} source <i class="far fa-up-right-from-square ml-1"></i></a>
                    <span v-else class="section-link">{{ scale.start_year }}-{{ scale.end_year }}</span>
                </section-header>
                <ui-card>
                    <salary-schedule-table :scale="scale" :position="position" :target="chartTarget" :targets="targets" @select="chartTarget = $event"/>
                </ui-card>
            </template>
        </template>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import EmptyState from "../framework/EmptyState";
    import UiCard from "../UiCard";
    import LaneTargetCards from "./LaneTargetCards";
    import SalaryScheduleTable from "./SalaryScheduleTable";
    import PackagePitch from "../package/PackagePitch";
    import salaryLane from "./salaryLane";
    import {pdRoute} from "@/utils/pd";

    /**
     * Professional Development > Salary Schedule: the career profile that
     * places the student, the columns clock hours can reach (with bundle
     * sizing), the district's published grid, and the stay-vs-move charts.
     * The student-side counterpart of the v2 quote builder's Information
     * pane.
     */
    export default {
        name: "PdSalaryView",
        components: {SectionHeader, EmptyState, UiCard, LaneTargetCards, SalaryScheduleTable, PackagePitch},
        mixins: [salaryLane],
        data() {
            return {picked: null};
        },
        computed: {
            ...mapGetters(['pdSummary']),
            pd() {
                return this.pdSummary;
            },
            settingsRoute() {
                return pdRoute('settings');
            },
            // The lane the charts follow: the student's pick, else the next
            // column up
            chartTarget: {
                get() {
                    if (this.picked && this.targets.some(t => t.tier === this.picked.tier)) return this.picked;
                    return this.firstTarget;
                },
                set(t) {
                    this.picked = t;
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .cards-intro {
        color: #555;
        margin-bottom: 1rem;
    }

    .section-link {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.85rem;

        &:hover {
            color: white;
        }
    }
</style>
